/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from "three";

import { const_var, params } from '../../../../../redux/reducers/reducer';
import { controls } from '../../../../../redux/reducers/common/eventReducer';

import { extrudeSettings, railModel } from '../../../../../utils/Functions';
import BaseRailModel from '../../BaseRail';
import PillarRailModel from '../../Legs';
import CommonTruss from '../CommonTruss';
import { MaterialTruss } from '../../../CustomMaterial';
import { trussDimensions } from '../../../../../helpers/building_component/truss/truss.helper';

const roofRailDistanceY = 0.7;
const hangRailDivision = 2 / 3;

const LST_RNT = ({ trussType, normalMap, envMap, trussModelPosInfoArr, overlapTrussArr, hiddenBaseRailIdArr, meshDelModel_CSG, isFor  }) => {

    return (
        <>
            {isFor == 'center' && <BaseRailModel normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} />}
            {isFor == 'center' && <PillarRailModel trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} roofRailDistanceY={roofRailDistanceY} hangRailDivision={hangRailDivision} />}
            <TrussModel trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} isFor={isFor}/>
        </>
    )
}

export default LST_RNT

const TrussModel = ({ trussType, trussModelPosInfoArr, normalMap, envMap, overlapTrussArr, hiddenBaseRailIdArr, isFor }) => {
    const obj_centerVRail = new THREE.Object3D();
    const obj_centerHRail = new THREE.Object3D();
    const obj_connectVShortRail = new THREE.Object3D();
    const obj_underTruss = new THREE.Object3D();
    const vector = new THREE.Vector3(1, 0, 0)

    const ref_centerVRail = useRef();
    const ref_centerHRail = useRef();
    const ref_connectVShortRail = useRef();
    const ref_underTruss = useRef();

    const bottomBaseRailWidth = 0.3;
    const bottomBaseRailHeight = 0.1;
    const connectRailWidth = 0.3;
    const connectRailHeight = 0.2;
    const offset = 0.035;
    const trussDimension = trussDimensions(isFor)

    const alignPosOffset = useMemo(() => {
        if(params.p_r_s === 1 ) return const_var.roofRegularDst * 2;
        else if(params.p_r_s === 2 || params.p_r_s === 3) return 0;
        else return 0;
    }, [params.p_r_s, trussDimension.roofPitch])

    const roofAlpha = Math.atan(trussDimension.roofPitch / 12);
    const roofHeight = (trussDimension.buildingWidth / 2) * (trussDimension.roofPitch / 12);
    const roofOuterRailLength = useMemo(() => (Math.sqrt(Math.pow(trussDimension.buildingWidth / 2, 2) + Math.pow(roofHeight, 2))), [trussDimension.buildingWidth, roofHeight]);
    const bottomBaseRailLength = (trussDimension.buildingWidth - bottomBaseRailHeight / 2 - connectRailWidth / 2) * hangRailDivision;
    const roofCenterRailHeight = ((trussDimension.buildingWidth / 2) * trussDimension.roofPitch / 12) * hangRailDivision + roofRailDistanceY;

    const connectRailDataArr = useMemo(() => {
        let dataInfoArray = [];
        let count, distance;

        let initDistance = 0.9;
        const tempCount = Math.floor((trussDimension.buildingWidth / 2) / initDistance);
        if ((trussDimension.buildingWidth / 2) === tempCount * initDistance) {
            count = tempCount;
            distance = initDistance;
        } else if ((trussDimension.buildingWidth / 2) - tempCount * initDistance <= initDistance / 2) {
            count = tempCount;
            distance = (trussDimension.buildingWidth / 2) / count;
        } else {
            count = tempCount + 1;
            distance = (trussDimension.buildingWidth / 2) / count;
        }

        for (let i = 1; i <= count; i++) {
            const pos_x = distance * i - connectRailWidth / 2;
            const pos_y = trussDimension.buildingHeight + (trussDimension.buildingWidth / 2 - pos_x) * (trussDimension.roofPitch / 12) - roofRailDistanceY;
            const verticalRailLength = roofRailDistanceY;
            
            const tempInfoObj = {
                distance: distance,
                pos_x: pos_x,
                pos_y: pos_y,
                length: (params.p_r_s === 1 && i === count) ? 0 : verticalRailLength,
            }

            dataInfoArray.push(tempInfoObj);
        }

        return dataInfoArray
    }, [trussDimension.buildingHeight, trussDimension.buildingWidth, trussDimension.roofPitch])  

    const connectCenterRailModelDataArr = useMemo(() => {
        let dataInfoArray = [];
        let count, distance;

        let initDistance = 4.5;
        const availableWidth = (trussDimension.buildingWidth / 2) * 2 / 3
        const tempCount = Math.floor((availableWidth) / initDistance);
        if ((availableWidth) === tempCount * initDistance) {
            count = tempCount;
            distance = initDistance;
        } else if ((availableWidth) - tempCount * initDistance <= initDistance / 2) {
            count = tempCount;
            distance = (availableWidth) / count;
        } else {
            count = tempCount + 1;
            distance = (availableWidth) / count;
        }

        for (let i = 1; i < count; i++) {
            const pos_x_v_rail = distance * i;
            const pos_x_s_rail = distance * (i - 1);
            const verticalRailHeight = (trussDimension.roofPitch / 12 * (trussDimension.buildingWidth / 2 - pos_x_v_rail) - connectRailHeight / 2) - (roofHeight / 3 - roofRailDistanceY) - roofRailDistanceY ;
            const slopeRailHeight = Math.sqrt(Math.pow(verticalRailHeight, 2) + Math.pow(distance, 2));

            const alphaDeviation = 0.97;
            const slopeRailAlpha = (Math.PI / 2 - Math.atan(verticalRailHeight / distance)) / alphaDeviation;
            
            const tempInfoObj = {
                distance: distance,
                pos_x_v_rail: pos_x_v_rail,
                pos_x_s_rail: pos_x_s_rail,
                verticalRailHeight: verticalRailHeight,
                slopeRailHeight: slopeRailHeight,
                slopeRailAlpha: slopeRailAlpha
            }

            dataInfoArray.push(tempInfoObj);
                
        }

        return dataInfoArray
    }, [trussDimension.buildingWidth, roofHeight, trussDimension.roofPitch])

    // const railConnectModel = useMemo(() =>{
    //     const meshModel = new THREE.Mesh(new THREE.ExtrudeGeometry(railModel(connectRailWidth, connectRailHeight), extrudeSettings(sideEdgeSlopeRailLength - 1.3)), new THREE.MeshStandardMaterial({ color: 'brown' , side: THREE.DoubleSide}))
    //     const meshDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(railModel(connectRailWidth, connectRailHeight + 0.5), extrudeSettings(0.2)), new THREE.MeshStandardMaterial({ color: 'green' , side: THREE.DoubleSide}))

    //     const meshModel_Temp = meshModel.clone()
    //     meshModel_Temp.updateMatrixWorld();

    //     const meshDelModel_Temp = meshDelModel.clone()
    //     meshDelModel_Temp.position.set(0, -0.05, sideEdgeSlopeRailLength - 0.8 - (trussDimension.roofPitch*0.1));
    //     meshDelModel_Temp.rotateX(-roofAlpha - Math.PI / 4.6);
        
    //     meshDelModel_Temp.updateMatrixWorld();

    //     const meshModel_CSG = CSG.fromMesh(meshModel_Temp)
    //     const meshDelModel_CSG = CSG.fromMesh(meshDelModel_Temp)

    //     const Model_CSG = meshModel_CSG.subtract(meshDelModel_CSG)
    //     const CSGMeshModel = CSG.toMesh(
    //         Model_CSG,
    //         new THREE.Matrix4(),
    //         new THREE.MeshStandardMaterial({ color: 'grey', side:THREE.DoubleSide })
    //     )

    //     return CSGMeshModel
    // }, [roofAlpha])
    

    useEffect(() => {
        if (ref_centerHRail.current && ref_centerVRail.current && ref_connectVShortRail.current && ref_underTruss.current) {
            let index_all_1 = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 1) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    //----- roof under rail-----
                    obj_underTruss.position.set(offsetForDir * (-trussDimension.buildingWidth / 2 + connectRailWidth / 4), trussDimension.buildingHeight - connectRailHeight / 2 - roofRailDistanceY + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_underTruss.rotation.y = offsetForDir * Math.PI / 2;
                    obj_underTruss.rotateOnAxis(vector, offsetForDir * roofAlpha);
                    //--------------------------

                    //----- bottom base rail-----
                    obj_centerHRail.position.set(-bottomBaseRailLength / 2, trussDimension.buildingHeight + roofHeight - roofCenterRailHeight - bottomBaseRailHeight / 2 + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_centerHRail.rotation.y = Math.PI / 2;
                    //---------------------

                    //----- roof center rail-----
                    obj_centerVRail.position.set(0, trussDimension.buildingHeight + roofHeight - roofCenterRailHeight - bottomBaseRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_centerVRail.rotation.x = -Math.PI / 2;
                    //---------------------

                    if (hiddenBaseRailIdArr.includes(index_truss)) {
                        obj_underTruss.scale.setScalar(0);
                        obj_centerHRail.scale.setScalar(0);
                        obj_centerVRail.scale.setScalar(0);
                    } else {
                        obj_underTruss.scale.setScalar(1);
                        obj_centerHRail.scale.setScalar(1);
                        obj_centerVRail.scale.setScalar(1);
                    }

                    obj_underTruss.updateMatrix();
                    ref_underTruss.current.setMatrixAt(index_all_1, obj_underTruss.matrix);
                    obj_centerVRail.updateMatrix();
                    ref_centerVRail.current.setMatrixAt(index_all_1, obj_centerVRail.matrix);
                    obj_centerHRail.updateMatrix();
                    ref_centerHRail.current.setMatrixAt(index_all_1, obj_centerHRail.matrix);

                    index_all_1 ++;
                })
            })

            let index_all_2 = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    new Array(connectRailDataArr.length).fill('').forEach((_, index_rail) => {
                        obj_connectVShortRail.position.set(offsetForDir * connectRailDataArr[index_rail].pos_x, connectRailDataArr[index_rail].pos_y - bottomBaseRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                        obj_connectVShortRail.rotation.x = -Math.PI / 2;

                        if ((hiddenBaseRailIdArr.includes(index_truss)) || (index_rail % 2 === 1)) {
                            obj_connectVShortRail.scale.setScalar(0);
                        } else {
                            obj_connectVShortRail.scale.setScalar(1);
                        }

                        if (connectRailDataArr[index_rail].length === 0) obj_connectVShortRail.scale.setScalar(0);

                        obj_connectVShortRail.updateMatrix();
                        ref_connectVShortRail.current.setMatrixAt(index_all_2, obj_connectVShortRail.matrix);

                        index_all_2 ++;
                    })
                })
            })
            
            ref_centerHRail.current.instanceMatrix.needsUpdate = true;
            ref_centerVRail.current.instanceMatrix.needsUpdate = true;
            ref_connectVShortRail.current.instanceMatrix.needsUpdate = true;
            ref_underTruss.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_centerHRail.current, ref_centerVRail.current, ref_connectVShortRail.current, ref_underTruss.current, trussDimension.buildingWidth, trussDimension.buildingHeight, trussModelPosInfoArr, overlapTrussArr, hiddenBaseRailIdArr, connectRailDataArr, roofHeight, roofCenterRailHeight, alignPosOffset, roofAlpha, trussType, connectCenterRailModelDataArr, controls.isCheckActiveTab, params.activeWall])

    return (
        <group position={[0, -offset, 0]} rotation={[0,trussDimension.trussRotation,0]} name='LST-RNT-truss-legs'>
            <CommonTruss trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} normalMap={normalMap} envMap={envMap} roofRailDistanceY={roofRailDistanceY} isFor={isFor}/>
            <>
                {/* --- roof unnder truss model --- */}
                <instancedMesh ref={ref_underTruss} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(roofOuterRailLength - 0.04)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- bottom base rail model --- */}
                <instancedMesh ref={ref_centerHRail} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(bottomBaseRailWidth, bottomBaseRailHeight), extrudeSettings(bottomBaseRailLength)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- roof center rail model --- */}
                <instancedMesh ref={ref_centerVRail} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(roofCenterRailHeight)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- connect rail model --- */}
                <instancedMesh ref={ref_connectVShortRail} args={[null, null, trussModelPosInfoArr.length * connectRailDataArr.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(roofRailDistanceY)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {connectCenterRailModelDataArr.map((_, index) =>
                    <group name='LST-RNT-truss-rail-legs'>
                        <ConnectVLongRailModel
                            index={index}
                            trussModelPosInfoArr={trussModelPosInfoArr}
                            connectCenterRailModelDataArr={connectCenterRailModelDataArr}
                            bottomBaseRailHeight={bottomBaseRailHeight}
                            roofHeight={roofHeight}
                            roofRailDistanceY={roofRailDistanceY}
                            alignPosOffset={alignPosOffset}
                            hiddenBaseRailIdArr={hiddenBaseRailIdArr}
                            overlapTrussArr={overlapTrussArr}
                            connectRailHeight={connectRailHeight}
                            connectRailWidth={connectRailWidth}
                            roofAlpha={roofAlpha}
                            mainColor={"#F0F0F0"}
                            normalMap={normalMap}
                            envMap={envMap}
                            isFor={isFor}
                        />
                        <ConnectSRailModel
                            index={index}
                            trussModelPosInfoArr={trussModelPosInfoArr}
                            connectCenterRailModelDataArr={connectCenterRailModelDataArr}
                            bottomBaseRailHeight={bottomBaseRailHeight}
                            roofHeight={roofHeight}
                            roofRailDistanceY={roofRailDistanceY}
                            alignPosOffset={alignPosOffset}
                            hiddenBaseRailIdArr={hiddenBaseRailIdArr}
                            overlapTrussArr={overlapTrussArr}
                            connectRailHeight={connectRailHeight}
                            connectRailWidth={connectRailWidth}
                            roofAlpha={roofAlpha}
                            mainColor={"#F0F0F0"}
                            normalMap={normalMap}
                            envMap={envMap}
                            isFor={isFor}
                        />
                    </group>
                )}
            </>
        </group>
    )
}

const ConnectVLongRailModel = ({ index, trussModelPosInfoArr, connectCenterRailModelDataArr, bottomBaseRailHeight, alignPosOffset, hiddenBaseRailIdArr, overlapTrussArr, connectRailHeight, connectRailWidth, roofAlpha, mainColor, normalMap, envMap, roofHeight, roofRailDistanceY, isFor }) => {
    const obj_connectVLongRail = new THREE.Object3D();
    const ref_connectVLongRail  = useRef();
    const trussDimension = trussDimensions(isFor)

    const updateInstancedMesh = useCallback(() => {
        if (ref_connectVLongRail.current) {

            let index_all = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    obj_connectVLongRail.position.set(offsetForDir * connectCenterRailModelDataArr[index].pos_x_v_rail, trussDimension.buildingHeight + roofHeight / 3 - roofRailDistanceY - bottomBaseRailHeight / 2 + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_connectVLongRail.rotation.x = -Math.PI / 2;

                    if (hiddenBaseRailIdArr.includes(index_truss)) {
                        obj_connectVLongRail.scale.setScalar(0);
                    } else {
                        obj_connectVLongRail.scale.setScalar(1);
                    }

                    obj_connectVLongRail.updateMatrix();
                    ref_connectVLongRail.current.setMatrixAt(index_all, obj_connectVLongRail.matrix);
                    
                    index_all ++;
                })
            })
            
            ref_connectVLongRail.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_connectVLongRail.current, trussDimension.buildingWidth, trussDimension.buildingHeight, params.singleSlope, params.freeStandingSide, trussModelPosInfoArr, overlapTrussArr, trussDimension.buildingWidth, trussDimension.buildingHeight, roofAlpha, connectCenterRailModelDataArr, hiddenBaseRailIdArr, alignPosOffset])
    
    useLayoutEffect(() => {
        updateInstancedMesh();
    }, [updateInstancedMesh]);
    
    return (
        <instancedMesh ref={ref_connectVLongRail} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
            <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(connectCenterRailModelDataArr[index].verticalRailHeight)]} />
            <MaterialTruss normalMap={normalMap} envMap={envMap} />
        </instancedMesh>
    )
}

const ConnectSRailModel = ({ index, trussModelPosInfoArr, connectCenterRailModelDataArr, bottomBaseRailHeight, alignPosOffset, hiddenBaseRailIdArr, overlapTrussArr, connectRailHeight, connectRailWidth, roofAlpha, mainColor, normalMap, envMap, roofHeight, roofRailDistanceY, isFor }) => {
    const obj_connectSRail = new THREE.Object3D();
    const ref_connectSRail = useRef();
    const vector = new THREE.Vector3(1, 0, 0)
    const trussDimension = trussDimensions(isFor)

    const updateInstancedMesh = useCallback(() => {
        if (ref_connectSRail.current) {

            let index_all = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    obj_connectSRail.position.set(offsetForDir * connectCenterRailModelDataArr[index].pos_x_s_rail, trussDimension.buildingHeight + roofHeight / 3 - roofRailDistanceY - bottomBaseRailHeight + connectRailWidth / 2 + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_connectSRail.rotation.y = offsetForDir * -Math.PI / 2;
                    obj_connectSRail.rotateOnAxis(vector, offsetForDir * (-Math.PI / 2 - connectCenterRailModelDataArr[index].slopeRailAlpha));

                    if (hiddenBaseRailIdArr.includes(index_truss)) {
                        obj_connectSRail.scale.setScalar(0);
                    } else {
                        obj_connectSRail.scale.setScalar(1);
                    }
                    obj_connectSRail.updateMatrix();
                    ref_connectSRail.current.setMatrixAt(index_all, obj_connectSRail.matrix);

                    index_all ++;
                })
            })

            ref_connectSRail.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_connectSRail.current, trussDimension.buildingWidth, trussDimension.buildingHeight, params.singleSlope, params.freeStandingSide, trussModelPosInfoArr, overlapTrussArr, trussDimension.buildingWidth, trussDimension.buildingHeight, roofAlpha, connectCenterRailModelDataArr, hiddenBaseRailIdArr, alignPosOffset])
    
    useLayoutEffect(() => {
        updateInstancedMesh();
    }, [updateInstancedMesh]);
    
    return (
        <instancedMesh ref={ref_connectSRail} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
            <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(connectCenterRailModelDataArr[index].slopeRailHeight)]} />
            <MaterialTruss normalMap={normalMap} envMap={envMap} />
        </instancedMesh>
    )
}